import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import useApi from "../../../../hooks/useApi";
import Spinner from "../../../utils/Spinner";
import CalendarControls from "./calendar/CalendarControls";
import DailyView from "./calendar/DailyView";
import WeeklyView from "./calendar/WeeklyView";
import MonthlyView from "./calendar/MonthlyView";
import BookingDetailModal from "./BookingDetailModal";
import { getRestaurantBookings, cancelBooking } from "../../../../services/bookingService";
import { getRestaurantCalendarOrders } from "../../../../services/orderService";
import { getTableOverview } from "../../../../services/tableService";
import { toast } from "../../../../utils/demoToast";

// Map order status IDs to readable status strings
const ORDER_STATUS_MAP = {
  1: "pending",     // started
  2: "pending",     // confirmedWithoutPayment
  3: "confirmed",   // confirmedByRestaurant
  4: "confirmed",   // deliveringInProgress
  5: "completed",   // successfullyDone
  6: "canceled",    // canceledByCustomer
  7: "canceled",    // refunded
  8: "declined",    // refusedByRestaurant
  9: "pending",     // itemAdded
  10: "pending",    // itemRemoved
  11: "pending",    // userAdded
  12: "pending",    // userRemoved
  13: "confirmed",  // paymentConfirmed
  14: "declined",   // paymentRefused
  15: "canceled",   // refundRequested
  16: "pending",    // refundRefused
};

const BUSINESS_CONSUMPTION_MODE_ID = 3;

/**
 * Determine the color theme for a calendar/table item:
 * - booking → "danger"
 * - order with business consumption mode → "info"
 * - order (non-business) → "warning"
 */
export const getItemColorTheme = (item) => {
  if (item.type !== "order") return "danger";
  if (item.consumptionModeId === BUSINESS_CONSUMPTION_MODE_ID) return "info";
  return "warning";
};

/**
 * Normalize a booking object into a unified calendar item
 */
const normalizeBooking = (booking) => {
  const normalized = {
    ...booking,
    type: "booking",
    itemId: `booking-${booking.bookingId}`,
    displayName: booking.guestName || "—",
    displayDate: booking.bookingDate,
    displayPartySize: booking.partySize,
    displayStatus: booking.status,
  };
  normalized.colorTheme = getItemColorTheme(normalized);
  return normalized;
};

/**
 * Normalize an order object into a unified calendar item
 */
const normalizeOrder = (order) => {
  const masterUser = order.masterUser;
  const displayName = masterUser
    ? `${masterUser.firstname || ""} ${masterUser.lastname || ""}`.trim()
    : order.orderCode || `#${order.orderId}`;

  const normalized = {
    ...order,
    type: "order",
    itemId: `order-${order.orderId}`,
    displayName,
    displayDate: order.mealDate || order.startAt,
    displayPartySize: order.nbPerson || 1,
    displayStatus: ORDER_STATUS_MAP[order.orderStatusId] || "pending",
    bookingDate: order.mealDate || order.startAt,
    guestName: displayName,
    partySize: order.nbPerson || 1,
    status: ORDER_STATUS_MAP[order.orderStatusId] || "pending",
    guestEmail: masterUser?.email || null,
    guestPhone: masterUser?.phone || null,
    bookingCode: order.orderCode || `OF-${order.orderId}`,
    tableBookings: [],
  };
  normalized.colorTheme = getItemColorTheme(normalized);
  return normalized;
};

const BookingCalendarView = ({
  defaultDuration = 2,
  hideCanceled = false,
  tKey = "dashboard.bookings.",
}) => {
  const { t } = useTranslation();
  const restaurantId = useSelector((state) => state.user.restaurantId);
  const controlledRestaurantId = useSelector(
    (state) => state.admin.controlledRestaurantId
  );
  const activeRestaurantId = controlledRestaurantId || restaurantId;

  const [viewMode, setViewMode] = useState("daily");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tables, setTables] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filterTable, setFilterTable] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const viewRef = useRef(null);

  // Exit fullscreen on Escape key
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setIsFullscreen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen]);

  const { request: fetchBookings, loading: bookingsLoading } =
    useApi(getRestaurantBookings);
  const { request: fetchOrders, loading: ordersLoading } =
    useApi(getRestaurantCalendarOrders);
  const { request: fetchTables, loading: tablesLoading } =
    useApi(getTableOverview);
  const { request: cancelBookingRequest } = useApi(cancelBooking);

  // Compute the date range to fetch based on view mode
  const getDateRange = useCallback(() => {
    const from = new Date(currentDate);
    const to = new Date(currentDate);

    switch (viewMode) {
      case "daily":
        // Fetch the whole day
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        break;
      case "weekly": {
        // Monday to Sunday
        const dayOfWeek = from.getDay();
        from.setDate(from.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
        from.setHours(0, 0, 0, 0);
        to.setTime(from.getTime());
        to.setDate(to.getDate() + 6);
        to.setHours(23, 59, 59, 999);
        break;
      }
      case "monthly":
        from.setDate(1);
        from.setHours(0, 0, 0, 0);
        to.setMonth(to.getMonth() + 1, 0);
        to.setHours(23, 59, 59, 999);
        break;
      default:
        break;
    }

    return {
      from: from.toISOString().split("T")[0],
      to: to.toISOString().split("T")[0],
    };
  }, [currentDate, viewMode]);

  const fetchData = useCallback(async () => {
    if (!activeRestaurantId) return;

    try {
      const { from, to } = getDateRange();
      const bookingParams = { from, to, limit: 500 };

      if (filterStatus) {
        bookingParams.status = filterStatus;
      }

      const [bookingsRes, ordersRes, tablesRes] = await Promise.all([
        fetchBookings(activeRestaurantId, bookingParams),
        fetchOrders(activeRestaurantId, from, to),
        fetchTables(activeRestaurantId),
      ]);

      setBookings(bookingsRes.data?.data || bookingsRes.data?.rows || []);
      setOrders(ordersRes.data || []);
      setTables(tablesRes.data?.rooms || []);
    } catch (error) {
      console.error("Error fetching booking/order data:", error);
      toast.error(t(`${tKey}errors.fetchBookings`));
    }
  }, [
    activeRestaurantId,
    getDateRange,
    filterStatus,
    fetchBookings,
    fetchOrders,
    fetchTables,
    t,
    tKey,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Normalize and merge bookings + orders into a single unified list
  const allItems = useMemo(() => {
    const normalizedBookings = bookings.map(normalizeBooking);
    const normalizedOrders = orders
      .filter((o) => o.mealDate)
      .map(normalizeOrder);
    return [...normalizedBookings, ...normalizedOrders];
  }, [bookings, orders]);

  // Filter items client-side for table filter
  const filteredItems = useMemo(() => {
    let items = allItems;

    // Hide canceled/declined items
    if (hideCanceled) {
      items = items.filter(
        (item) => item.displayStatus !== "canceled" && item.displayStatus !== "declined"
      );
    }

    // Filter by table
    if (filterTable) {
      items = items.filter((item) =>
        item.tableBookings?.some((tb) => String(tb.tableId) === String(filterTable))
      );
    }

    // Filter by status (if set and it affects orders too)
    if (filterStatus) {
      items = items.filter((item) => item.displayStatus === filterStatus);
    }

    return items;
  }, [allItems, hideCanceled, filterTable, filterStatus]);

  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    switch (viewMode) {
      case "daily":
        newDate.setDate(newDate.getDate() + (direction === "next" ? 1 : -1));
        break;
      case "weekly":
        newDate.setDate(newDate.getDate() + (direction === "next" ? 7 : -7));
        break;
      case "monthly":
        newDate.setMonth(
          newDate.getMonth() + (direction === "next" ? 1 : -1)
        );
        break;
      default:
        break;
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm(t(`${tKey}confirm.cancelBooking`))) return;
    try {
      await cancelBookingRequest(bookingId);
      toast.success(t(`${tKey}success.bookingCanceled`));
      setSelectedItem(null);
      fetchData();
    } catch (error) {
      console.error("Error canceling booking:", error);
      toast.error(t(`${tKey}errors.cancelBooking`));
    }
  };

  // When clicking a day in monthly/weekly view, switch to daily
  const handleSelectDate = (day) => {
    setCurrentDate(day);
    setViewMode("daily");
  };

  const loading = bookingsLoading || ordersLoading || tablesLoading;

  if (loading && allItems.length === 0) {
    return (
      <div className="flex justify-center align-center p-5">
        <Spinner />
      </div>
    );
  }

  return (
    <div
      ref={viewRef}
      className={`bookings-calendar-view ${isFullscreen ? "bookings-calendar-view--fullscreen" : ""}`}
    >
      <CalendarControls
        viewMode={viewMode}
        setViewMode={setViewMode}
        currentDate={currentDate}
        navigateDate={navigateDate}
        goToToday={goToToday}
        filterTable={filterTable}
        setFilterTable={setFilterTable}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        tables={tables}
      />

      <div className="bookings-calendar-container mt-3">
        {viewMode === "daily" && (
          <DailyView
            date={currentDate}
            items={filteredItems}
            tables={tables}
            defaultDuration={defaultDuration}
            onViewItem={setSelectedItem}
            isFullscreen={isFullscreen}
            onToggleFullscreen={() => setIsFullscreen((prev) => !prev)}
          />
        )}
        {viewMode === "weekly" && (
          <WeeklyView
            date={currentDate}
            items={filteredItems}
            onViewItem={setSelectedItem}
            onSelectDate={handleSelectDate}
            isFullscreen={isFullscreen}
            onToggleFullscreen={() => setIsFullscreen((prev) => !prev)}
          />
        )}
        {viewMode === "monthly" && (
          <MonthlyView
            date={currentDate}
            items={filteredItems}
            onSelectDate={handleSelectDate}
            isFullscreen={isFullscreen}
            onToggleFullscreen={() => setIsFullscreen((prev) => !prev)}
          />
        )}
      </div>

      <BookingDetailModal
        item={selectedItem}
        tables={tables}
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        onCancel={handleCancelBooking}
      />
    </div>
  );
};

export default BookingCalendarView;
